import { Fragment, type ReactNode, useCallback, useMemo } from 'react';
import { ChartTooltip, PositionRelative, SvgChart } from '../lib/components';
import { Axes, Clipping, Grid, HoverMarker } from '../lib/components/Graph';
import { BottomMonths } from '../lib/components/Graph/Axes/Bottom';
import { Area, Bullet, Dots, Line } from '../lib/components/Shapes';
import { useScaleLinearX, useScaleLinearY } from '../lib/hooks/d3';
import { useCastNumbers, useFilterUnselected, useStacked } from '../lib/hooks/data';
import { max } from '../lib/utils';

function StackedAreas<T>({
  data: rawData,
  mappings,
  totalText,
  stacked = true,
  dotMappings,
  staticBottomAxis = false,
  includeMonthBottomAxis = false,
  onAreaClick,
}: {
  data: T[];
  mappings: {
    name: string | ReactNode;
    dataKey: keyof Omit<T, 'name'>;
    color: string;
    id: string;
    detailsDataKey?: keyof T;
  }[];
  dotMappings?: {
    name: string | ReactNode;
    dataKey: keyof Omit<T, 'name'>;
    color: string;
    square?: boolean;
    id: string;
  }[];
  stacked?: boolean;
  totalText?: string;
  staticBottomAxis?: boolean;
  includeMonthBottomAxis?: boolean;
  onAreaClick?: (item: any) => void;
}) {
  const data = useCastNumbers(rawData, mappings); // the passed data should rather be valid
  const filteredMappings = useFilterUnselected(mappings);
  const [stackedData, connectedMappings] = useStacked<T>(data, filteredMappings);

  const reverseMappings = [...connectedMappings].reverse();
  const maxKey = reverseMappings[0] && reverseMappings[0].dataKey;
  const maxValue = Math.ceil(max(stackedData, d => d[maxKey]) || 1);

  const scaleX = useScaleLinearX(0, stackedData.length - 1);
  const scaleY = useScaleLinearY(0, maxValue);
  const [y0] = scaleY.range();

  const getHoveredTick = useCallback((x: any) => Math.round(scaleX.invert(x)), [scaleX]);
  const hasDetails = useMemo(
    () => data.some((x: any) => x[mappings[0].detailsDataKey]),
    [data, mappings],
  );

  return (
    <PositionRelative>
      <SvgChart getHoveredTick={getHoveredTick}>
        <Grid scaleX={scaleX} scaleY={scaleY} />
        <Clipping>
          {(stacked ? reverseMappings : filteredMappings).map(obj => (
            <Fragment key={obj.id}>
              <Area
                data={stacked ? stackedData : data}
                keyY1={obj.dataKey}
                keyY0={stacked ? (obj as any).prev.dataKey : 0}
                scaleX={scaleX}
                scaleY={scaleY}
                color={obj.color}
                detailsKey={obj.detailsDataKey}
                onAreaClick={onAreaClick}
                cursor={hasDetails && onAreaClick ? 'pointer' : undefined}
              />
              <Line
                key={obj.id}
                data={stacked ? stackedData : data}
                keyY={obj.dataKey}
                scaleX={scaleX}
                scaleY={scaleY}
                stroke={obj.color}
              />
            </Fragment>
          ))}
        </Clipping>
        <Axes data={data} scaleY={scaleY} scaleX={scaleX} staticBottomAxis={staticBottomAxis} />
        {includeMonthBottomAxis && <BottomMonths data={data} extraPadding={24} y={y0} />}

        {dotMappings?.map(({ id, dataKey, color, square }) => (
          <Dots
            id={id}
            key={id}
            data={rawData}
            keyY={dataKey}
            square={square}
            scaleX={scaleX}
            scaleY={scaleY}
            color={color}
          />
        ))}
        <HoverMarker
          data={stacked ? stackedData : data}
          scaleX={scaleX}
          scaleY={scaleY}
          mappings={reverseMappings}
          Point={Bullet}
        />
      </SvgChart>
      <ChartTooltip
        data={rawData}
        scaleX={scaleX}
        dataSeries={[
          ...filteredMappings,
          ...(dotMappings ?? []).map(s => ({ ...s, hiddenOnZero: true })),
        ]}
        totalText={totalText}
      />
    </PositionRelative>
  );
}

export default StackedAreas;
